import { DatabaseDiff, YDatabase } from '@databases/collab/y-database';
import { DefaultRootState } from '@databases/redux';
import {
  BasicDatabase,
  BrokenDatabase,
  Database,
  ShallowDatabase,
} from '@databases/types/database';
import { IntlShape } from 'react-intl-next';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { Action } from '@canvas/model/permissions';

import { DATABASE_CONTENT_TYPE } from '../../constants';
import { getDatabaseContentByContentId } from '../../database/service';
import { serializeError } from '../../utils';
import {
  buildDatabaseFromCollabAndContent,
  getOrCreateCollabManager,
} from '../collab/actions';
import { loadAsync } from '../redux';

import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdRequired,
  selectDatabaseByContentIdStatus,
} from './selectors';
import databasesSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

const fakeDatabaseContent = {
  status: 'current',
  _links: {},
  version: {
    number: 1,
    friendlyWhen: '',
    message: '',
    when: '',
    by: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: '',
      },
    },
  },
  history: {
    createdDate: '',
    createdBy: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: '',
      },
    },
    lastUpdated: {
      number: 1,
      message: '',
      friendlyWhen: '',
      when: '',
      by: {
        type: 'anonymous',
        displayName: '',
        profilePicture: {
          path: '',
        },
      },
    },
  },
} as const;

/**
 * Load the specified database into the store. If the database is already loaded, no request is made.
 */
export const loadDatabaseByContentId =
  (
    contentId: string,
    intl: IntlShape,
    {
      forceReload = false,
      throttle = false,
    }: {
      forceReload?: boolean;
      throttle?: boolean;
    } = {}
  ): Dispatcher<Promise<Database | BrokenDatabase>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectDatabaseByContentIdStatus(getState(), { contentId }),
      () => {
        dispatch(databasesSlice.actions.loadDatabaseByContentIdPending({ contentId }));
      },
      async () => {
        const existingDatabase = selectDatabaseByContentIdOptional(getState(), {
          contentId,
        });
        if (forceReload || !existingDatabase) {
          const databaseContent = await getDatabaseContentByContentId(contentId);
          if (!databaseContent || databaseContent.type !== DATABASE_CONTENT_TYPE) {
            throw new Error(`no content for database with content id ${contentId}`);
          }

          const spaceId = databaseContent.space?.id;
          const referenceId = databaseContent.referenceId;

          if (!spaceId) {
            throw new Error(`no space id for database with content id ${contentId}`);
          }

          if (!referenceId) {
            throw new Error(`no reference id for database with content id ${contentId}`);
          }

          const database: Database | BrokenDatabase = await new Promise(
            async (resolveDatabase) => {
              const manager = await dispatch(
                getOrCreateCollabManager(
                  {
                    contentId,
                    spaceId,
                    referenceId,
                  },
                  intl
                )
              );

              let didLoad = false;

              // assume read/write permissions for now
              manager.onPermissionsUpdate(databaseContent.referenceId, {
                board: new Set([Action.read, Action.write]),
              });

              manager.onLoaded(async (db) => {
                const isReadOnly = manager.isReadOnly;

                if (!didLoad) {
                  await resolveDatabase(
                    buildDatabaseFromCollabAndContent(databaseContent, db, isReadOnly)
                  );
                  didLoad = true;
                }
              });

              // kick off connection attempt
              manager.connect();
            }
          );

          return database;
        }

        return existingDatabase;
      },
      async (database) => {
        dispatch(addDatabaseToList(database));
        dispatch(databasesSlice.actions.loadDatabaseByContentIdFinished({ contentId }));
      },
      (error) => {
        dispatch(
          databasesSlice.actions.loadDatabaseByContentIdError({
            contentId,
            error: serializeError(error),
          })
        );
      },
      { forceReload, throttle }
    );

    return selectDatabaseByContentIdRequired(getState(), { contentId });
  };

/**
 * Load the specified database into the store. If the database is already loaded, no request is made.
 */
export const loadDatabaseByReferenceId =
  (
    {
      contentId,
      referenceId,
      spaceId,
      spaceKey,
      intl,
      title,
      spaceName,
    }: {
      contentId: string;
      referenceId: string;
      spaceId: number;
      spaceKey: string;
      intl: IntlShape;
      title: string;
      spaceName: string;
    },
    {
      forceReload = false,
      throttle = false,
    }: {
      forceReload?: boolean;
      throttle?: boolean;
    } = {}
  ): Dispatcher<Promise<Database | BrokenDatabase>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectDatabaseByContentIdStatus(getState(), { contentId }),
      () => {
        dispatch(databasesSlice.actions.loadDatabaseByContentIdPending({ contentId }));
      },
      async () => {
        const existingDatabase = selectDatabaseByContentIdOptional(getState(), {
          contentId,
        });
        if (forceReload || !existingDatabase) {
          const database: Database | BrokenDatabase = await new Promise(
            async (resolveDatabase) => {
              const manager = await dispatch(
                getOrCreateCollabManager(
                  {
                    contentId,
                    spaceId,
                    referenceId,
                  },
                  intl
                )
              );

              let didLoad = false;

              // assume read/write permissions for now
              manager.onPermissionsUpdate(referenceId, {
                board: new Set([Action.read, Action.write]),
              });

              manager.onLoaded(async (db) => {
                const isReadOnly = manager.isReadOnly;

                if (!didLoad) {
                  await resolveDatabase(
                    buildDatabaseFromCollabAndContent(
                      {
                        id: contentId,
                        referenceId,
                        space: { id: spaceId, key: spaceKey, name: spaceName },
                        type: DATABASE_CONTENT_TYPE,
                        title,
                        ...fakeDatabaseContent,
                      },
                      db,
                      isReadOnly
                    )
                  );
                  didLoad = true;
                }
              });

              // kick off connection attempt
              manager.connect();
            }
          );
          return database;
        }

        return existingDatabase;
      },
      async (database) => {
        dispatch(addDatabaseToList(database));
        dispatch(databasesSlice.actions.loadDatabaseByContentIdFinished({ contentId }));
      },
      (error) => {
        dispatch(
          databasesSlice.actions.loadDatabaseByContentIdError({
            contentId,
            error: serializeError(error),
          })
        );
      },
      { forceReload, throttle }
    );

    return selectDatabaseByContentIdRequired(getState(), { contentId });
  };

/**
 * Add a new database to the list of databases. If a database with the same content ID exists already, it is replaced (unless the added
 * database is shallow and a non-shallow database is already in the store).
 */
export const addDatabaseToList = (
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): AnyAction => addDatabasesToList([database]);

export const addDatabasesToList = (
  databases: Array<Database | ShallowDatabase | BasicDatabase | BrokenDatabase>
): AnyAction =>
  databasesSlice.actions.addDatabasesToList({
    databases,
  });

export const applyDatabaseDiff = (diffInfo: {
  contentId: string;
  diff: DatabaseDiff;
  collabDatabase: YDatabase;
  isReadOnly: boolean;
}): AnyAction => databasesSlice.actions.applyDatabaseDiff(diffInfo);
